/**
 * utils/costItems.js
 * Pure cost-item-row validation and type/label lookup (Brief 1 of the cost-items rollout).
 *
 * A cost item is an itemized extra amount on a transaction, separate from its goods lines —
 * "biaya" (a cost paid to someone other than the transaction's other party — commission, tip,
 * self-arranged shipping) or "tagihan"/"tagihanSupplier" (an amount that will later raise
 * Total). This brief only stores and validates the shape; what "tagihan" does to Total is a
 * later brief's job (see the root CLAUDE.md's Cost items entry).
 *
 * Deliberately its own file, mirroring payments.js's own "every engine here gets its own file
 * per concern" precedent — but a single tier suffices here, unlike payments.js's validatePay-
 * mentSet/validatePayments split, since cost items have no creation-time "intent" to validate
 * against (no Lunas/Belum-Lunas-style total-matching rule).
 *
 * Brief 2 (wiring into total/Hutang/Piutang/Lunas) added the two sum helpers below — the
 * type-filtering knowledge ("which types raise total, which don't") lives here, alongside
 * costItemTypesFor, so txnStatus.js's txnTotal/totalDikurangiBiaya and TransactionFormModal's
 * own client-side total never have to duplicate the type check independently.
 *
 * Corrective pass, Piece 2: adds a genuine third type, "potongan" (discount) — the mirror
 * image of tagihan/tagihanSupplier, LOWERS total instead of raising it. Available on all three
 * transaction types, appended to the end of both type arrays (neutral, raises, lowers — the
 * existing reading order). `costItemTypesFor`/`costItemTypeLabel`/`validateCostItems` are all
 * already generic over these arrays, so this addition needed no other code change in this file.
 */

// Biaya Lain-Lain (2026-10-11, the cost system the client confirmed): the "Rincian Tambahan"
// box offers only "+ Tagihan" and "− Potongan". Our own costs ("biaya") moved OUT of costItems
// into their own list, `data.expenses` (utils/expenses.js) — the v26 migration (storage.js)
// moved every stored "biaya" row there, so a "biaya" row is no longer a valid type to save. A
// Pembelian's tagihan keeps its stored type "tagihanSupplier" (no migration) but is labeled
// plain "Tagihan" everywhere.
export const COST_ITEM_TYPES_SALES = [
  { value: "tagihan", label: "Tagihan", buttonLabel: "+ Tagihan" },
  { value: "potongan", label: "Potongan", buttonLabel: "− Potongan" },
];

export const COST_ITEM_TYPES_PEMBELIAN = [
  { value: "tagihanSupplier", label: "Tagihan", buttonLabel: "+ Tagihan" },
  { value: "potongan", label: "Potongan", buttonLabel: "− Potongan" },
];

/** PENJUALAN/TRANSFER share one type pair; PEMBELIAN's tagihan is stored as "tagihanSupplier". */
export function costItemTypesFor(txnType) {
  return txnType === "PEMBELIAN" ? COST_ITEM_TYPES_PEMBELIAN : COST_ITEM_TYPES_SALES;
}

// Labels for types that only appear in an OLD edit-log entry: "biaya" (moved to Biaya
// Lain-Lain by the v26 migration) and "biayaLain" (a Biaya Lain-Lain change logged on its
// transaction — App.js's editTransaction folds those into costChanges).
const HISTORY_LABEL = { biaya: "Biaya", biayaLain: "Biaya Lain-Lain" };

export function costItemTypeLabel(type, txnType) {
  const match = costItemTypesFor(txnType).find((t) => t.value === type);
  return match ? match.label : HISTORY_LABEL[type] || type;
}

/**
 * Validate a draft's cost-item rows against the transaction's own type — non-empty
 * description, a non-negative numeric amount, and a type valid for this txnType.
 *
 * @returns {{ ok: boolean, error: string|null }}
 */
export function validateCostItems(items, txnType) {
  const allowed = costItemTypesFor(txnType).map((t) => t.value);
  for (const it of items) {
    if (!it.description || !it.description.trim()) {
      return { ok: false, error: "Deskripsi wajib diisi" };
    }
    if (typeof it.amount !== "number" || Number.isNaN(it.amount) || it.amount < 0) {
      return { ok: false, error: "Jumlah tidak boleh negatif" };
    }
    if (!allowed.includes(it.type)) {
      return { ok: false, error: `tipe biaya tidak valid: ${it.type}` };
    }
  }
  return { ok: true, error: null };
}

/**
 * Σ amount across every row whose type raises a transaction's total — "tagihan" (Penjualan/
 * Transfer) or "tagihanSupplier" (Pembelian). A "biaya" row never contributes here — that money
 * goes to a third party, not the transaction's own counterparty (see the root CLAUDE.md's Cost
 * items entry for the full rule).
 */
export function sumCostItemsRaisingTotal(costItems) {
  return (costItems || []).reduce(
    (s, c) => s + (c.type === "tagihan" || c.type === "tagihanSupplier" ? c.amount : 0), 0
  );
}

/** Σ amount across every "biaya"-type row — the amount totalDikurangiBiaya subtracts back out
 *  (Penjualan/Transfer) or totalDitambahBiaya adds back in (Pembelian). */
export function sumBiayaCostItems(costItems) {
  return (costItems || []).reduce((s, c) => s + (c.type === "biaya" ? c.amount : 0), 0);
}

/** Σ amount across every "potongan"-type row — a discount the transaction's own counterparty
 *  owes/is owed LESS because of, subtracted out of txnTotal (Piece 2 of the corrective pass). */
export function sumPotonganCostItems(costItems) {
  return (costItems || []).reduce((s, c) => s + (c.type === "potongan" ? c.amount : 0), 0);
}

/**
 * Per-item color for a Tagihan/Tagihan Supplier/Potongan row — extracted from
 * TxnCostCells.js's own RincianTambahanCell so this exact rule has ONE home, shared by the
 * dashboard cell and TransactionDetailModal's "Tagihan & Potongan" box (the "Tagihan &
 * Potongan" rename task). Callers are expected to have already filtered to these three types.
 */
export function costItemColor(type) {
  return type === "potongan" ? "var(--green)" : "var(--red)";
}

/**
 * Sign-based color for the aggregate net figure (Σtagihan/tagihanSupplier − Σpotongan) —
 * positive (raises what's owed) = red, negative (lowers it) = green, exactly zero = neutral
 * (a cancel-out is "nothing happened," not a genuine reduction). Same extraction as
 * costItemColor above.
 */
export function costItemNetColor(net) {
  return net > 0 ? "var(--red)" : net < 0 ? "var(--green)" : "var(--text)";
}
